import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../config/supabase';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Button,
  Stack,
  Alert,
  IconButton,
  Badge,
  Divider,
  Chip,
  Paper,
  Avatar
} from '@mui/material';
import {
  SportsEsports,
  EmojiEvents,
  Star,
  TrendingUp,
  CheckCircle,
  Timer,
  LocalFireDepartment,
  Psychology,
  ArrowBack,
  Lock,
  PlayArrow
} from '@mui/icons-material';

const Challenges = () => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [loading, setLoading] = useState(true);
  const [challenges, setChallenges] = useState([]);
  const [userProgress, setUserProgress] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Mock challenge data - in a real app, this would come from the database
  const mockChallenges = [
    {
      id: 1,
      title: "7-Day Gratitude Challenge",
      description: "Write down 3 things you're grateful for each day",
      category: "mindfulness",
      difficulty: "beginner",
      duration: "7 days",
      points: 100,
      participants: 1234,
      completed: false,
      progress: 3,
      total: 7,
      icon: "🙏",
      color: "#4CAF50"
    },
    {
      id: 2,
      title: "30-Day Meditation Streak",
      description: "Meditate for at least 10 minutes every day",
      category: "meditation",
      difficulty: "intermediate",
      duration: "30 days",
      points: 300,
      participants: 856,
      completed: false,
      progress: 12,
      total: 30,
      icon: "🧘",
      color: "#9C27B0"
    },
    {
      id: 3,
      title: "Anxiety Warrior Week",
      description: "Complete anxiety-reducing exercises daily",
      category: "anxiety",
      difficulty: "beginner",
      duration: "7 days",
      points: 150,
      participants: 2341,
      completed: true,
      progress: 7,
      total: 7,
      icon: "⚡",
      color: "#FF9800"
    },
    {
      id: 4,
      title: "Self-Love Journey",
      description: "Practice self-compassion exercises for 14 days",
      category: "self-care",
      difficulty: "beginner",
      duration: "14 days",
      points: 200,
      participants: 1876,
      completed: false,
      progress: 0,
      total: 14,
      icon: "💖",
      color: "#E91E63"
    },
    {
      id: 5,
      title: "Stress Buster Challenge",
      description: "Learn and practice 5 different stress management techniques",
      category: "stress",
      difficulty: "intermediate",
      duration: "10 days",
      points: 250,
      participants: 1543,
      completed: false,
      progress: 2,
      total: 10,
      icon: "🌊",
      color: "#2196F3"
    },
    {
      id: 6,
      title: "Confidence Builder",
      description: "Daily confidence-boosting activities and affirmations",
      category: "confidence",
      difficulty: "advanced",
      duration: "21 days",
      points: 350,
      participants: 987,
      completed: false,
      progress: 0,
      total: 21,
      icon: "💪",
      color: "#FF5722"
    }
  ];

  const categories = [
    { id: 'all', label: 'All Challenges', icon: '🏆' },
    { id: 'journaling', label: 'Journaling', icon: '📝' },
    { id: 'meditation', label: 'Meditation', icon: '🧘' },
    { id: 'conversation', label: 'Conversations', icon: '💬' },
    { id: 'streak', label: 'Streaks', icon: '�' },
    { id: 'wellness', label: 'Wellness', icon: '😊' },
    { id: 'combination', label: 'Combination', icon: '🌟' }
  ];

  const getDifficultyLabel = (level) => {
    switch (level) {
      case 1: return 'beginner';
      case 2: return 'intermediate';
      case 3: return 'advanced';
      default: return 'beginner';
    }
  };

  const getCategoryIcon = (category) => {
    const iconMap = {
      'journaling': '📝',
      'meditation': '🧘',
      'conversation': '�',
      'streak': '🔥',
      'wellness': '😊',
      'combination': '🌟'
    };
    return iconMap[category] || '🏆';
  };

  const getCategoryColor = (category) => {
    const colorMap = {
      'journaling': '#4CAF50',
      'meditation': '#9C27B0',
      'conversation': '#2196F3',
      'streak': '#FF5722',
      'wellness': '#FF9800',
      'combination': '#673AB7'
    };
    return colorMap[category] || '#757575';
  };

  const calculateProgress = (progressData) => {
    // Calculate actual progress based on the challenge requirements
    if (progressData.status === 'completed') return progressData.challenge_details?.duration_days || 7;
    
    if (progressData.status === 'active') {
      const { progress_data } = progressData;
      const requirements = progressData.challenge_details?.requirements;
      
      if (!progress_data || !requirements) {
        // Fallback to days-based calculation
        const startDate = new Date(progressData.started_at);
        const now = new Date();
        const daysDiff = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
        return Math.min(daysDiff, progressData.challenge_details?.duration_days || 7);
      }
      
      // Calculate based on actual activities completed
      let totalCompleted = 0;
      let totalRequired = 0;
      
      if (requirements.journal_entries) {
        totalRequired += requirements.journal_entries;
        totalCompleted += progress_data.journal_entries || 0;
      }
      
      if (requirements.meditation_sessions) {
        totalRequired += requirements.meditation_sessions;
        totalCompleted += progress_data.meditation_sessions || 0;
      }
      
      if (requirements.conversations) {
        totalRequired += requirements.conversations;
        totalCompleted += progress_data.conversations || 0;
      }
      
      // Return actual completed activities, not percentage
      return totalCompleted;
    }
    
    return 0;
  };

  // Helper function to determine challenge type from title/description if not provided
  const getChallengeType = (challenge) => {
    // If we have the challenge_type or type field, use it
    if (challenge.challenge_type) return challenge.challenge_type;
    if (challenge.type) return challenge.type;
    
    // Otherwise, map based on title
    const title = challenge.title?.toLowerCase() || '';
    if (title.includes('journal starter')) return 'journal_beginner';
    if (title.includes('consistent journal')) return 'journal_consistent';
    if (title.includes('journal dedication')) return 'journal_dedicated';
    if (title.includes('first meditation')) return 'meditation_first';
    if (title.includes('meditation week')) return 'meditation_week';
    if (title.includes('meditation mastery')) return 'meditation_mastery';
    if (title.includes('conversation starter')) return 'conversation_starter';
    if (title.includes('conversation explorer')) return 'conversation_explorer';
    if (title.includes('activity streak')) return 'journal_beginner'; // Maps to 3 days
    if (title.includes('wellbeing champion')) return 'wellbeing_champion'; // Maps to 30 total
    if (title.includes('super active week')) return 'super_active_week'; // Maps to 10 activities
    
    // Default fallback
    return 'journal_consistent';
  };

  const getRequiredCount = (challengeType) => {
    const requirements = {
      'journal_beginner': 3,
      'journal_consistent': 7,
      'journal_dedicated': 15,
      'meditation_first': 1,
      'meditation_week': 5,
      'meditation_mastery': 20,
      'conversation_starter': 5,
      'conversation_explorer': 15,
      'wellbeing_champion': 30,
      'super_active_week': 10,
    };
    return requirements[challengeType] || 7;
  };

  // Helper function to get current progress for any challenge type
  const getCurrentProgress = (challenge, progressData) => {
    if (!progressData?.actualProgressData) return 0;
    
    const challengeType = getChallengeType(challenge);
    
    if (challengeType === 'wellbeing_champion' || challengeType === 'super_active_week') {
      // Sum all activity types for combined challenges
      const journals = progressData.actualProgressData.journal_entries || 0;
      const meditations = progressData.actualProgressData.meditation_sessions || 0;
      const conversations = progressData.actualProgressData.conversations || 0;
      return journals + meditations + conversations;
    } else if (challengeType?.includes('journal')) {
      return progressData.actualProgressData.journal_entries || 0;
    } else if (challengeType?.includes('meditation')) {
      return progressData.actualProgressData.meditation_sessions || 0;
    } else if (challengeType?.includes('conversation')) {
      return progressData.actualProgressData.conversations || 0;
    }
    
    return 0;
  };

  // Helper function to check if challenge is actually completed based on progress
  const isActuallyCompleted = (challenge, progressData) => {
    if (!progressData?.actualProgressData) return false;
    
    const requiredCount = getRequiredCount(getChallengeType(challenge));
    const currentProgress = getCurrentProgress(challenge, progressData);
    
    return currentProgress >= requiredCount;
  };

  useEffect(() => {
    fetchChallenges();
    if (session?.user) {
      fetchUserProgress();
    }
  }, [session]);

  // Refresh progress every 30 seconds when user is active
  useEffect(() => {
    if (!session?.user) return;

    const refreshInterval = setInterval(() => {
      fetchUserProgress();
    }, 30000); // 30 seconds

    return () => clearInterval(refreshInterval);
  }, [session]);

  // Add a manual refresh function that can be called after journal entries
  const refreshChallengeProgress = async () => {
    if (session?.user) {
      await fetchUserProgress();
      await fetchChallenges();
    }
  };

  // Listen for custom events (you can dispatch these from your journal component)
  useEffect(() => {
    const handleProgressUpdate = () => {
      refreshChallengeProgress();
    };

    window.addEventListener('challengeProgressUpdate', handleProgressUpdate);
    return () => window.removeEventListener('challengeProgressUpdate', handleProgressUpdate);
  }, [session]);

  const fetchChallenges = async () => {
    try {
      setLoading(true);
      
      // Fetch challenges from backend API
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Add auth header if user is logged in
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch('https://backend.sanityreps.com/api/challenges', {
        method: 'GET',
        headers
      });

      if (response.ok) {
        const challengesData = await response.json();
        // Transform API data to match our component structure
        const transformedChallenges = challengesData.map(challenge => ({
          id: challenge.id,
          title: challenge.title,
          description: challenge.description,
          category: challenge.category,
          difficulty: getDifficultyLabel(challenge.difficulty),
          duration: `${challenge.duration_days} days`,
          points: challenge.xp_reward,
          participants: 0, // Will be updated from stats if available
          completed: challenge.userStatus?.isCompleted || false,
          progress: 0, // Will be calculated based on user progress
          total: challenge.duration_days,
          icon: getCategoryIcon(challenge.category),
          color: getCategoryColor(challenge.category),
          userStatus: challenge.userStatus || {
            isJoined: false,
            status: null,
            isActive: false,
            isCompleted: false
          }
        }));
        
        setChallenges(transformedChallenges);
      } else {
        console.error('Failed to fetch challenges:', response.status);
        // Fallback to mock data if API fails
        setChallenges(mockChallenges);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching challenges:', error);
      // Fallback to mock data if API fails
      setChallenges(mockChallenges);
      setLoading(false);
    }
  };

  const fetchUserProgress = async () => {
    if (!session?.access_token) return;
    try {
      const response = await fetch('https://backend.sanityreps.com/api/challenges/progress', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      const progressData = await response.json();
      
      // DEBUG: Show detailed info about each challenge
      progressData.forEach((challenge, index) => {
      });

      if (Array.isArray(progressData)) {
        const progressMap = {};
        progressData.forEach(progress => {
          progressMap[progress.challenge_type] = {
            progress: calculateProgress(progress),
            completed: progress.status === 'completed',
            isJoined: true,
            isActive: progress.status === 'active',
            xp_earned: progress.xp_earned,
            actualProgressData: progress.progress_data
          };
        });
        setUserProgress(progressMap);
      } else {
        console.error('Expected array but got:', typeof progressData, progressData);
        setUserProgress({});
        // Optionally show a message to the user here
      }
    } catch (error) {
      console.error('Error fetching user progress:', error);
    }
  };

  const handleJoinChallenge = async (challengeId) => {
    if (!session?.access_token) {
      alert('Please log in to join challenges.');
      return;
    }

    try {

      // Optimistically update the UI
      setChallenges(prevChallenges => 
        prevChallenges.map(challenge => 
          challenge.id === challengeId 
            ? { 
                ...challenge, 
                userStatus: {
                  ...challenge.userStatus,
                  isJoined: true,
                  isActive: true,
                  status: 'active'
                }
              }
            : challenge
        )
      );

      // Make API call to join challenge using the correct endpoint
      const response = await fetch(`https://backend.sanityreps.com/api/challenges/${challengeId}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update user progress state
        setUserProgress(prev => ({
          ...prev,
          [challengeId]: {
            progress: 1,
            completed: data.isCompleted || false,
            isJoined: data.isJoined || true,
            isActive: data.isActive || true
          }
        }));

        // Refresh challenges data to get latest state
        await fetchChallenges();
        
      } else {
        console.error('Failed to join challenge:', response.status);
        const errorData = await response.json().catch(() => ({}));
        
        // Revert optimistic update on failure
        setChallenges(prevChallenges => 
          prevChallenges.map(challenge => 
            challenge.id === challengeId 
              ? { 
                  ...challenge, 
                  userStatus: {
                    ...challenge.userStatus,
                    isJoined: false,
                    isActive: false,
                    status: null
                  }
                }
              : challenge
          )
        );
        
        alert(errorData.message || 'Failed to join challenge. Please try again.');
      }
    } catch (error) {
      console.error('Error joining challenge:', error);
      
      // Revert optimistic update on error
      setChallenges(prevChallenges => 
        prevChallenges.map(challenge => 
          challenge.id === challengeId 
            ? { 
                ...challenge, 
                userStatus: {
                  ...challenge.userStatus,
                  isJoined: false,
                  isActive: false,
                  status: null
                }
              }
            : challenge
        )
      );
      
      alert('Error joining challenge. Please check your connection and try again.');
    }
  };

  const filteredChallenges = selectedCategory === 'all' 
    ? challenges 
    : challenges.filter(challenge => challenge.category === selectedCategory);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return '#4CAF50';
      case 'intermediate': return '#FF9800';
      case 'advanced': return '#F44336';
      default: return '#757575';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Typography variant="h6">Loading challenges...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: '#fafbfc', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0', mb: 3 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={() => navigate('/chat')} sx={{ mr: 2 }}>
                <ArrowBack />
              </IconButton>
              <SportsEsports sx={{ mr: 1, color: '#FF5722' }} />
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#1a237e' }}>
                Wellness Challenges
              </Typography>
            </Box>
            <Button
              variant="outlined"
              onClick={refreshChallengeProgress}
              startIcon={<TrendingUp />}
              sx={{ ml: 2 }}
            >
              Refresh Progress
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ pb: 4 }}>
        {/* Category Filter */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Filter by Category</Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {categories.map((category) => (
              <Chip
                key={category.id}
                label={`${category.icon} ${category.label}`}
                onClick={() => setSelectedCategory(category.id)}
                variant={selectedCategory === category.id ? 'filled' : 'outlined'}
                color={selectedCategory === category.id ? 'primary' : 'default'}
                sx={{ mb: 1 }}
              />
            ))}
          </Stack>
        </Paper>

        {/* Challenges Grid */}
        <Grid container spacing={3}>
          {filteredChallenges.map((challenge) => (
            <Grid item xs={12} md={6} lg={4} key={challenge.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 3
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  {/* Challenge Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: challenge.color,
                        mr: 2,
                        width: 48,
                        height: 48,
                        fontSize: '1.5rem'
                      }}
                    >
                      {challenge.icon}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                        {challenge.title}
                      </Typography>
                      <Chip 
                        label={challenge.difficulty}
                        size="small"
                        sx={{ 
                          bgcolor: getDifficultyColor(challenge.difficulty),
                          color: 'white',
                          fontWeight: 500,
                          mt: 0.5
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Description */}
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ mb: 2, minHeight: 48 }}
                  >
                    {challenge.description}
                  </Typography>

                  {/* Progress */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Progress
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {getCurrentProgress(challenge, userProgress[challenge.id])}/{getRequiredCount(getChallengeType(challenge))}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={(getCurrentProgress(challenge, userProgress[challenge.id]) / getRequiredCount(getChallengeType(challenge))) * 100}
                      sx={{ 
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: challenge.color,
                          borderRadius: 4
                        }
                      }}
                    />
                  </Box>

                  {/* Stats */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Timer sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {challenge.duration}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Star sx={{ fontSize: 16, mr: 0.5, color: '#FFD700' }} />
                      <Typography variant="body2" color="text.secondary">
                        {challenge.points} pts
                      </Typography>
                    </Box>
                  </Box>

                  {/* Participants */}
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    👥 {challenge.participants.toLocaleString()} participants
                  </Typography>

                  {/* Action Button */}
                  <Button
                    fullWidth
                    variant={isActuallyCompleted(challenge, userProgress[challenge.id]) ? "outlined" : "contained"}
                    color={isActuallyCompleted(challenge, userProgress[challenge.id]) ? "success" : "primary"}
                    startIcon={
                      isActuallyCompleted(challenge, userProgress[challenge.id]) ? <CheckCircle /> : 
                      (challenge.userStatus?.isActive || userProgress[challenge.id]?.isActive) ? <PlayArrow /> : <TrendingUp />
                    }
                    onClick={() => handleJoinChallenge(challenge.id)}
                    disabled={
                      isActuallyCompleted(challenge, userProgress[challenge.id]) || 
                      challenge.userStatus?.isActive || 
                      userProgress[challenge.id]?.isActive
                    }
                    sx={{ 
                      bgcolor: isActuallyCompleted(challenge, userProgress[challenge.id]) ? 'transparent' : challenge.color,
                      '&:hover': {
                        bgcolor: isActuallyCompleted(challenge, userProgress[challenge.id]) ? 'transparent' : challenge.color,
                        opacity: 0.8
                      },
                      '&:disabled': {
                        bgcolor: 'transparent',
                        color: challenge.userStatus?.isActive || userProgress[challenge.id]?.isActive ? 'primary.main' : 'success.main'
                      }
                    }}
                  >
                    {isActuallyCompleted(challenge, userProgress[challenge.id]) ? 'Completed!' : 
                     (challenge.userStatus?.isActive || userProgress[challenge.id]?.isActive) ? 'In Progress' : 'Join Challenge'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Empty State */}
        {filteredChallenges.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <SportsEsports sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No challenges found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try selecting a different category or check back later for new challenges.
            </Typography>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default Challenges;
